import { createContext, useContext, type ReactNode } from "react";
import {
  useNotifications,
  type AppNotification,
} from "./hooks/useNotifications";

/**
 * Shared notifications state for the seller portal. Opening the Firestore
 * feed (`onSnapshot`, limit 50) is expensive, so exactly ONE subscription is
 * mounted here — high in the tree — and both the desktop `NotificationBell`
 * (AppShell top bar) and the `MobileHeaderNotificationBell` (rendered by every
 * `MobileScreenHeader`, which mounts its action row twice) read from it via
 * `useNotificationsContext`. This replaces the former per-bell `useNotifications`
 * call that opened two listeners per mobile screen.
 */
type NotificationsContextValue = {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

export function NotificationsProvider({
  sellerId,
  storeId,
  children,
}: {
  sellerId: string | undefined;
  storeId: string | undefined;
  children: ReactNode;
}) {
  const value = useNotifications(sellerId, storeId);
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider",
    );
  }
  return ctx;
}
